/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import FilterUtils from '../utils/FilterUtils';
import api from './api';
import { GLOBAL_OVERVIEW_SHIFT_ROUTES, GLOBAL_OVERVIEW_SHIFT_STATUS } from '../constants/ShiftStatus';
import { GLOBAL_CALENDAR_SHIFT_TYPES } from '../constants';

const globalFiltersInitialState = {
	resourceTypes: [],
	units: [],
	statuses: FilterUtils.convertGlobalOverviewShiftStatusToOptions(GLOBAL_OVERVIEW_SHIFT_STATUS),
	shiftRoutes: FilterUtils.convertGlobalOverviewShiftStatusToOptions(GLOBAL_OVERVIEW_SHIFT_ROUTES),
	selectedCaregiverFilter: null,
	features: []
};

const emptyResponseHandler = async (response) => {
	const responseText = await response.text();
	return responseText === 'OK' ? responseText : JSON.parse(responseText);
};

const apiWithTags = api.enhanceEndpoints({
	addTagTypes: ['GlobalOverviewShifts', 'GlobalOverviewShiftStructures']
});

const globalOverviewAPI = apiWithTags.injectEndpoints({
	tagTypes: ['GlobalOverviewShifts', 'GlobalOverviewShiftStructures'],
	endpoints: (build) => ({
		shifts: build.query({
			query: ({ start, end, type = GLOBAL_CALENDAR_SHIFT_TYPES.ALL }) =>
				`/flex/global-overview/shifts?start=${start}&end=${end}&type=${type}`,
			providesTags: ['GlobalOverviewShifts']
		}),
		shiftStructures: build.query({
			query: ({ type = GLOBAL_CALENDAR_SHIFT_TYPES.ALL }) => `/flex/global-overview/shift-structures?type=${type}`,
			providesTags: ['GlobalOverviewShifts']
		}),
		reopenShift: build.mutation({
			query: ({ slot }) => ({
				url: `/flex/slot/${slot.slot_id}/reopen`,
				method: 'PUT',
				headers: {
					'management-company-id': slot.management_company_id
				},
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		cancelSlot: build.mutation({
			query: ({ shiftType, slotId, ...payloadBody }) => ({
				url: shiftType === GLOBAL_CALENDAR_SHIFT_TYPES.FLEX ? `/flex/slot/${slotId}/cancel` : `/slot/${slotId}/cancel`,
				method: 'PUT',
				body: payloadBody,
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		recurringOpenSlots: build.mutation({
			query: ({ shiftId, shiftDate, isRecurring = true }) => ({
				url: `/recurring-shifts/${shiftId}/open-slots`,
				method: 'POST',
				body: { isRecurring, shiftDate }
			}),
			invalidatesTags: (_result, error) => (error?.data?.errors[0]?.field === 'shiftId' ? ['GlobalOverviewShifts'] : [])
		}),
		routeSlot: build.mutation({
			query: ({ slot }) => ({
				url: `/flex/slot/${slot.slot_id}/route-slot`,
				method: 'PUT',
				body: {
					oldUnitId: slot.unit_id
				},
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		facilityFloatSlot: build.mutation({
			query: ({ slot, targetFacilityId, caregiverId, structureId, unitId }) => ({
				url: `/flex/management-companies/${slot.management_company_id}/slots/${slot.slot_id}/facility-float`,
				method: 'POST',
				headers: {
					'management-company-id': slot.management_company_id
				},
				body: {
					targetFacilityId,
					caregiverId,
					structureId,
					unitId
				}
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		getFacilitiesToFloatSlot: build.query({
			query: ({ slotId, managementCompanyId }) => ({
				url: `/flex/management-companies/${managementCompanyId}/slots/${slotId}/float-available-facilities`,
				method: 'GET',
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		}),
		markSlotAsCallBack: build.mutation({
			query: ({ slotId, facilityId }) => ({
				url: `/on-call/facility/${facilityId}/slots/${slotId}/callback`,
				method: 'PUT',
				responseHandler: emptyResponseHandler
			}),
			invalidatesTags: ['GlobalOverviewShifts']
		})
	})
});

export const {
	useShiftsQuery,
	useReopenShiftMutation,
	useShiftStructuresQuery,
	useCancelSlotMutation,
	useRecurringOpenSlotsMutation,
	useRouteSlotMutation,
	useFacilityFloatSlotMutation,
	useGetFacilitiesToFloatSlotQuery,
	useMarkSlotAsCallBackMutation
} = globalOverviewAPI;

export const slice = createSlice({
	name: 'globalOverview',
	initialState: {
		periodIndex: 0,
		selectedFacilities: [],
		globalFilters: globalFiltersInitialState
	},
	reducers: {
		savePeriodIndex: (state, action) => {
			state.periodIndex = action.payload;
		},
		saveSelectedFacilities: (state, action) => {
			state.selectedFacilities = action.payload;
		},
		saveFilters: (state, action) => {
			state.globalFilters = action.payload;
		}
	}
});

export const resetApiState = () => api.util.resetApiState();

export const globalOverviewState = (state) => state.globalOverview;

export const { savePeriodIndex, saveSelectedFacilities, saveFilters } = slice.actions;

export default slice.reducer;
